import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

/**
 * PDP bundle tiers ("1 tee / 2 tees / 3 tees").
 *
 * Markup is Liquid-rendered (blocks/bundle-tiers.liquid); this element owns
 * the state: which tier is chosen, and for every extra slot which design
 * (product) and which variant. Slot 1 is always the main product form's
 * variant — the variant picker and the price block keep owning it, this
 * component only mirrors its price through the ProductSelectEvent.
 *
 * product-form.js asks `collectItems()` on submit when a tier above 1 is
 * selected and posts every line in one `/cart/add.js` call. Nothing here
 * talks to the cart directly.
 *
 * @typedef {{ id: number, title: string, available: boolean, price: number }} BundleVariant
 * @typedef {{ handle: string, title: string, image: string | null, options: string[], variants: BundleVariant[] }} BundleProduct
 * @typedef {{ index: number, product: BundleProduct, variantId: number | null, element: HTMLElement }} Slot
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} tiers
 * @property {HTMLScriptElement} productData
 * @property {HTMLTemplateElement} slotTemplate
 * @property {HTMLDialogElement} picker
 * @property {HTMLElement} pickerTitle
 * @property {HTMLElement} pickerGrid
 *
 * @extends {Component<Refs>}
 */
class BundleTiersComponent extends Component {
  requiredRefs = ['tiers', 'productData', 'slotTemplate', 'picker', 'pickerTitle', 'pickerGrid'];

  #abortController = new AbortController();

  /** @type {BundleProduct} */
  #currentProduct = { handle: '', title: '', image: null, options: [], variants: [] };

  /** The main form's variant price in minor units (slot 1). */
  #basePrice = 0;

  /** @type {Map<string, Promise<BundleProduct>>} */
  #productCache = new Map();

  /** @type {Map<number, Slot[]>} tier index → its extra slots */
  #slotsByTier = new Map();

  /** @type {Slot | null} the slot the picker dialog is editing */
  #pickingFor = null;

  /** @type {Text | null} the add-to-cart button's label text node */
  #addLabelNode = null;

  /** @type {string} */
  #addLabelOriginal = '';

  connectedCallback() {
    super.connectedCallback();

    this.#currentProduct = JSON.parse(this.refs.productData.textContent || '{}');
    this.#productCache.set(this.#currentProduct.handle, Promise.resolve(this.#currentProduct));
    this.#basePrice = Number(this.dataset.basePrice) || 0;

    const { signal } = this.#abortController;
    this.closest('dialog, .shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, {
      signal,
    });
    this.refs.picker.addEventListener('click', this.#onPickerBackdropClick, { signal });

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#restoreAddLabel();
  }

  /** @returns {number} 0-based index of the chosen tier */
  get tierIndex() {
    const checked = this.querySelector('.bundle-tier__input:checked');
    return checked instanceof HTMLInputElement ? Number(checked.value) : 0;
  }

  /** @returns {number} how many items the chosen tier adds */
  get count() {
    return Number(this.refs.tiers[this.tierIndex]?.dataset.count) || 1;
  }

  /** @returns {number} extra slots of the chosen tier that still have no size */
  get missingCount() {
    const slots = this.#slotsByTier.get(this.tierIndex) ?? [];
    return slots.filter((slot) => !slot.variantId).length;
  }

  /**
   * The lines to add for the chosen tier, slot 1 excluded (the form owns it).
   * Returns null — after nudging EVERY slot still missing a size — when an
   * extra slot has no variant chosen.
   * @returns {Array<{ variantId: string, quantity: number }> | null}
   */
  collectItems() {
    if (this.nudgeMissing() > 0) return null;
    const slots = this.#slotsByTier.get(this.tierIndex) ?? [];
    return slots.map((slot) => ({ variantId: String(slot.variantId), quantity: 1 }));
  }

  /**
   * Shows "Choose a size for Tee #n" and pulses the pills on every slot of
   * the chosen tier that has no size yet. product-form.js calls this from the
   * size gate as well, so a gated "Select a size" click flags slot 1 AND the
   * extra slots in one go.
   * @returns {number} how many slots were flagged
   */
  nudgeMissing() {
    const slots = this.#slotsByTier.get(this.tierIndex) ?? [];
    let missing = 0;
    for (const slot of slots) {
      if (slot.variantId) continue;
      this.#showSlotError(slot, missing === 0);
      missing += 1;
    }
    return missing;
  }

  /** @param {Event} event */
  selectTier(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#render();
  }

  /**
   * A size pill in a slot changed.
   * @param {Event} event
   */
  selectSize(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const slot = this.#slotFromElement(input);
    if (!slot) return;
    slot.variantId = Number(input.value);
    this.#hideSlotError(slot);
    this.#syncSlotLegend(slot);
    this.#renderPrices();
  }

  /** @param {Event} event */
  openPicker(event) {
    const slot = event.target instanceof Element ? this.#slotFromElement(event.target) : null;
    if (!slot) return;
    this.#pickingFor = slot;
    this.refs.pickerTitle.textContent = this.#fill(this.dataset.pickerTitle || '', slot.index);
    for (const button of this.refs.pickerGrid.querySelectorAll('.bundle-picker__product')) {
      if (!(button instanceof HTMLElement)) continue;
      button.setAttribute('aria-current', button.dataset.handle === slot.product.handle ? 'true' : 'false');
    }
    this.refs.picker.showModal();
  }

  closePicker() {
    this.#pickingFor = null;
    this.refs.picker.close();
  }

  /** @param {Event} event */
  async pickProduct(event) {
    const button = event.target instanceof Element ? event.target.closest('.bundle-picker__product') : null;
    const slot = this.#pickingFor;
    if (!(button instanceof HTMLElement) || !slot) return;

    const { handle, title, image, price } = button.dataset;
    if (!handle) return;

    this.closePicker();

    // Paint the chosen design immediately; sizes follow once the variants load.
    slot.product = { handle, title: title || '', image: image || null, options: [], variants: [] };
    slot.variantId = null;
    this.#renderSlotHead(slot);
    this.#renderSlotSizes(slot, true);
    this.#renderPrices(Number(price) || 0, slot);

    let product;
    try {
      product = await this.#fetchProduct(handle);
    } catch (error) {
      console.error(error);
      if (slot.product.handle === handle) this.#renderSlotSizes(slot);
      return;
    }
    if (slot.product.handle !== handle) return; // superseded by a later pick
    slot.product = product;
    slot.variantId = this.#defaultVariantId(product);
    this.#hideSlotError(slot);
    this.#renderSlotSizes(slot);
    this.#renderPrices();
  }

  /**
   * Slot 1 changed (variant picker): mirror its price and re-apply the add
   * label. Extra slots are never auto-filled from slot 1 — every size is the
   * shopper's explicit pick (user decision 2026-10-29).
   *
   * product-form.js morphs the add-to-cart button from the fetched HTML right
   * after this same promise settles (its listener registered later, so it runs
   * after ours); the label is re-read and re-applied on a macrotask so the
   * morph's "Add to cart" (or "Select a size") is what we wrap, not the other
   * way round.
   * @param {Event} event
   */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const promise = /** @type {Promise<{ detail?: { html?: Document, resource?: { id?: number, price?: number } | null } }> | undefined} */ (
      /** @type {any} */ (event).promise
    );
    if (!promise) return;
    promise
      .then(({ detail }) => {
        const variant = detail?.resource;
        if (variant && typeof variant.price === 'number') this.#basePrice = variant.price;
        const newButton = detail?.html?.querySelector('product-form-component [ref="addToCartButton"]');
        const newLabel = newButton ? this.#labelTextNode(newButton)?.textContent : null;
        setTimeout(() => {
          this.#addLabelNode = null;
          if (newLabel) this.#addLabelOriginal = newLabel;
          this.#renderPrices();
        }, 0);
      })
      .catch(() => {});
  };

  /** @param {MouseEvent} event */
  #onPickerBackdropClick = (event) => {
    if (event.target === this.refs.picker) this.closePicker();
  };

  /** Full render after a tier change: slots for the chosen tier, prices, add label. */
  #render() {
    const tierIndex = this.tierIndex;
    for (const tier of this.refs.tiers) {
      const index = Number(tier.dataset.tierIndex);
      const list = tier.querySelector('.bundle-tier__slots');
      if (!(list instanceof HTMLElement)) continue;
      const active = index === tierIndex;
      list.hidden = !active;
      if (active && !this.#slotsByTier.has(index)) {
        this.#buildSlots(index, list, Number(tier.dataset.count) || 1);
      }
    }
    this.#renderPrices();
  }

  /**
   * @param {number} tierIndex
   * @param {HTMLElement} list
   * @param {number} count
   */
  #buildSlots(tierIndex, list, count) {
    /** @type {Slot[]} */
    const slots = [];
    for (let index = 2; index <= count; index++) {
      const fragment = /** @type {DocumentFragment} */ (this.refs.slotTemplate.content.cloneNode(true));
      const element = /** @type {HTMLElement} */ (fragment.firstElementChild);
      element.dataset.slotIndex = String(index);
      list.appendChild(element);
      const product = this.#currentProduct;
      /** @type {Slot} */
      const slot = { index, product, variantId: this.#defaultVariantId(product), element };
      slots.push(slot);
      this.#renderSlotHead(slot);
      this.#renderSlotSizes(slot);
    }
    this.#slotsByTier.set(tierIndex, slots);
  }

  /** @param {Slot} slot */
  #renderSlotHead(slot) {
    const { element, product, index } = slot;
    const thumb = element.querySelector('.bundle-slot__thumb');
    if (thumb instanceof HTMLImageElement) {
      if (product.image) {
        thumb.src = product.image;
        thumb.hidden = false;
      } else {
        thumb.removeAttribute('src');
        thumb.hidden = true;
      }
    }
    const label = element.querySelector('.bundle-slot__label');
    if (label) label.textContent = this.#fill(this.dataset.slotLabel || '', index);
    const title = element.querySelector('.bundle-slot__title');
    if (title) title.textContent = product.title;
  }

  /**
   * @param {Slot} slot
   * @param {boolean} [loading] - variants still in flight: show a note instead of pills
   */
  #renderSlotSizes(slot, loading = false) {
    const { element, product } = slot;
    const pills = element.querySelector('.bundle-slot__pills');
    const name = element.querySelector('.bundle-slot__legend-name');
    const fieldset = element.querySelector('.bundle-slot__sizes');
    if (!(pills instanceof HTMLElement)) return;

    const hasOnlyDefault = product.variants.length === 1 && product.variants[0]?.title === 'Default Title';
    if (fieldset instanceof HTMLElement) fieldset.hidden = hasOnlyDefault && !loading;
    if (name) {
      const options = product.options.filter((option) => option !== 'Title');
      name.textContent = options.length ? options.join(' / ') : this.dataset.sizeWord || '';
    }

    pills.replaceChildren();
    if (loading) {
      const note = document.createElement('span');
      note.className = 'bundle-slot__loading';
      note.textContent = this.dataset.loading || '…';
      pills.appendChild(note);
      this.#syncSlotLegend(slot);
      return;
    }

    const soldOut = this.dataset.soldOut || '';
    for (const variant of product.variants) {
      const pill = document.createElement('label');
      pill.className = variant.available ? 'bundle-pill' : 'bundle-pill bundle-pill--sold-out';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `bundle-slot-${this.dataset.sectionId}-${slot.index}`;
      input.value = String(variant.id);
      input.setAttribute('on:change', '/selectSize');
      input.setAttribute('aria-label', variant.available ? variant.title : `${variant.title} - ${soldOut}`);
      input.disabled = !variant.available;
      input.checked = variant.id === slot.variantId;
      const text = document.createElement('span');
      text.textContent = variant.title;
      pill.append(input, text);
      pills.appendChild(pill);
    }
    this.#syncSlotLegend(slot);
  }

  /** @param {Slot} slot */
  #syncSlotLegend(slot) {
    const value = slot.element.querySelector('.bundle-slot__legend-value');
    if (!value) return;
    const variant = slot.product.variants.find((candidate) => candidate.id === slot.variantId);
    value.textContent = variant ? variant.title : '';
  }

  /**
   * Repaints every tier's compare / total / saving and the add-to-cart label.
   * Unselected tiers assume every slot is the main product at the current
   * variant price; the selected tier sums its real slots.
   * @param {number} [pendingPrice] - price to use for `pendingSlot` while its variants load
   * @param {Slot} [pendingSlot]
   */
  #renderPrices(pendingPrice, pendingSlot) {
    const tierIndex = this.tierIndex;
    for (const tier of this.refs.tiers) {
      const index = Number(tier.dataset.tierIndex);
      const count = Number(tier.dataset.count) || 1;
      const amount = Number(tier.dataset.amount) || 0;
      const slots = this.#slotsByTier.get(index) ?? [];
      let compare = this.#basePrice * count;
      if (index === tierIndex && slots.length) {
        compare = this.#basePrice;
        for (const slot of slots) compare += this.#slotPrice(slot, pendingSlot, pendingPrice);
      }
      const saving = amount * count;
      const total = Math.max(0, compare - saving);
      this.#setText(tier.querySelector('.bundle-tier__compare'), this.#money(compare));
      this.#setText(tier.querySelector('.bundle-tier__total'), this.#money(total));
      const save = tier.querySelector('.bundle-tier__save');
      if (save instanceof HTMLElement) {
        // Keep the Liquid-rendered sentence, swap only the amount it quotes.
        if (save.dataset.template === undefined) {
          save.dataset.template = (save.textContent || '').trim().replace(/\S+$/, '[amount]');
        }
        this.#setText(save, save.dataset.template.replace('[amount]', this.#moneyShort(saving)));
      }
      if (index === tierIndex) this.#applyAddLabel(count, total);
    }
  }

  /**
   * @param {Slot} slot
   * @param {Slot} [pendingSlot]
   * @param {number} [pendingPrice]
   */
  #slotPrice(slot, pendingSlot, pendingPrice) {
    if (slot === pendingSlot && typeof pendingPrice === 'number') return pendingPrice;
    const variant = slot.product.variants.find((candidate) => candidate.id === slot.variantId);
    if (variant) return variant.price;
    return slot.product.variants[0]?.price ?? this.#basePrice;
  }

  /**
   * "Add 2 to cart — $117.00" on the main button for tiers above 1; the
   * original label is restored on tier 1 (and on disconnect). A disabled
   * button ("Select a size" while the size gate is up, or sold out) keeps its
   * own text.
   * @param {number} count
   * @param {number} total
   */
  #applyAddLabel(count, total) {
    const node = this.#findAddLabelNode();
    if (!node) return;
    const button = node.parentElement;
    const hidden = this.getClientRects().length === 0; // e.g. CSS-hidden inside the quick-add modal
    const gated = button instanceof HTMLButtonElement && (button.disabled || button.dataset.sizeGate === 'true');
    if (count <= 1 || hidden || gated) {
      this.#restoreAddLabel();
      return;
    }
    const template = this.dataset.addLabel || '';
    node.textContent = template.replace('[count]', String(count)).replace('[total]', this.#money(total));
  }

  #restoreAddLabel() {
    if (this.#addLabelNode && this.#addLabelOriginal) this.#addLabelNode.textContent = this.#addLabelOriginal;
  }

  /** @returns {Text | null} */
  #findAddLabelNode() {
    if (this.#addLabelNode?.isConnected) return this.#addLabelNode;
    const button = this.#productForm()?.querySelector('[ref="addToCartButton"]');
    const node = button ? this.#labelTextNode(button) : null;
    if (!node) return null;
    this.#addLabelNode = node;
    if (!this.#addLabelOriginal) this.#addLabelOriginal = node.textContent || '';
    return node;
  }

  /**
   * The button's bare label text node (snippets/add-to-cart-button.liquid renders the label as a
   * direct text child between the icon and the checkmark burst).
   * @param {Element} button
   * @returns {Text | null}
   */
  #labelTextNode(button) {
    for (const node of button.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) return /** @type {Text} */ (node);
    }
    return null;
  }

  #productForm() {
    return this.closest('dialog, .shopify-section')?.querySelector('product-form-component') ?? null;
  }

  /**
   * `/products/<handle>.js`, normalised and cached per handle.
   * @param {string} handle
   * @returns {Promise<BundleProduct>}
   */
  #fetchProduct(handle) {
    let pending = this.#productCache.get(handle);
    if (!pending) {
      pending = fetch(`${(this.dataset.rootUrl || '/').replace(/\/$/, '')}/products/${handle}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Failed to fetch product: ${response.status}`);
          return response.json();
        })
        .then(
          /** @param {any} json */ (json) => ({
            handle: String(json.handle),
            title: String(json.title),
            image: json.featured_image
              ? `${json.featured_image}${json.featured_image.includes('?') ? '&' : '?'}width=160`
              : null,
            options: Array.isArray(json.options)
              ? json.options.map(/** @param {any} option */ (option) => (typeof option === 'string' ? option : option.name))
              : [],
            variants: Array.isArray(json.variants)
              ? json.variants.map(
                  /** @param {any} variant */ (variant) => ({
                    id: Number(variant.id),
                    title: String(variant.title),
                    available: Boolean(variant.available),
                    price: Number(variant.price),
                  })
                )
              : [],
          })
        );
      pending.catch(() => this.#productCache.delete(handle));
      this.#productCache.set(handle, pending);
    }
    return pending;
  }

  /**
   * Extra slots open with no size chosen — the shopper picks every size
   * explicitly, so the add path can refuse with "Choose a size for Tee #n".
   * A single-variant design (no sizes) is the one case that resolves itself.
   * @param {BundleProduct} product
   */
  #defaultVariantId(product) {
    if (product.variants.length === 1 && product.variants[0]?.title === 'Default Title' && product.variants[0].available) {
      return product.variants[0].id;
    }
    return null;
  }

  /**
   * @param {Slot} slot
   * @param {boolean} [scroll] - bring this slot into view (the first flagged one only)
   */
  #showSlotError(slot, scroll = true) {
    const error = slot.element.querySelector('.bundle-slot__error');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = this.#fill(this.dataset.sizeError || '', slot.index);
    error.hidden = false;
    // Re-run the pill pulse on every nudge (same trick as the main picker).
    slot.element.classList.remove('bundle-slot--nudge');
    void slot.element.offsetWidth;
    slot.element.classList.add('bundle-slot--nudge');
    if (scroll) slot.element.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  /** @param {Slot} slot */
  #hideSlotError(slot) {
    const error = slot.element.querySelector('.bundle-slot__error');
    if (error instanceof HTMLElement) error.hidden = true;
    slot.element.classList.remove('bundle-slot--nudge');
  }

  /** @param {Element} element */
  #slotFromElement(element) {
    const slotElement = element.closest('.bundle-slot');
    if (!slotElement) return null;
    for (const slots of this.#slotsByTier.values()) {
      const slot = slots.find((candidate) => candidate.element === slotElement);
      if (slot) return slot;
    }
    return null;
  }

  /**
   * @param {string} template
   * @param {number} index
   */
  #fill(template, index) {
    const unit = (this.dataset.unitSingular || '').replace(/^\p{L}/u, (letter) => letter.toUpperCase());
    return template.replace('[unit]', unit).replace('[index]', String(index));
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'USD');
  }

  /**
   * "$13.00" → "$13", "$6.50" stays (matches Liquid's money_without_trailing_zeros).
   * @param {number} cents
   */
  #moneyShort(cents) {
    return this.#money(cents).replace(/([.,])00(?!\d)/, '');
  }

  /**
   * @param {Element | null} element
   * @param {string} text
   */
  #setText(element, text) {
    if (element && element.textContent !== text) element.textContent = text;
  }
}

if (!customElements.get('bundle-tiers-component')) {
  customElements.define('bundle-tiers-component', BundleTiersComponent);
}
